import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import {
  debounceTime, distinctUntilChanged, filter, map, merge,
  Observable, OperatorFunction, Subject
} from 'rxjs';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { FormControl } from '@angular/forms';
import { OrganisationBase } from '../models/organisation-base';
import { OrganisationTree } from '../models/organisation-tree';

interface OrganisationSelectorEntry {
  level: number;
  organisation: OrganisationBase;
  disabled: boolean;
}

@Component({
    selector: 'app-organisation-selector',
    templateUrl: './organisation-selector.component.html',
    styleUrls: ['./organisation-selector.component.sass'],
    standalone: false
})
export class OrganisationSelectorComponent {

  private _orgTree!: OrganisationTree;

  @Input() set orgTree(orgTree: OrganisationTree) {
    this._orgTree = orgTree;
    this.computeOrganisations();
  };

  @Input() placeholder?: string | null = null;
  @Input() customId?: string | null = null;
  @Input() clearAfterSelection: boolean = false;

  private _disabledOrganisations: string[] = [];

  @Output() organisationSelected: EventEmitter<OrganisationBase | null> = new EventEmitter();

  @ViewChild('instance', { static: true }) instance!: NgbTypeahead;

  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  organisation: FormControl<OrganisationSelectorEntry | null> = new FormControl<OrganisationSelectorEntry | null>(null);

  organisations: OrganisationSelectorEntry[] = [];

  search: OperatorFunction<string, readonly { level: number; organisation: OrganisationBase }[]> = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(filter(() => !this.instance.isPopupOpen()));
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, clicksWithClosedPopup$, inputFocus$).pipe(
      map((term) => {
        if (term.length == 0) {
          // Reset to no organisation (i.e. all orgs selected) when text field empty
          this.organisationSelected.emit(null);
        }

        if (term.length < 2)
          return this.organisations;

        return this.organisations.filter(t => t.organisation.name.toLowerCase().indexOf(term.toLowerCase()) > -1)
      }),
    );
  };

  resultFormatter = (result: OrganisationSelectorEntry) => {
    return '\xa0\xa0'.repeat(result.level) + `${result.organisation.name} (${result.organisation.organisationRef})`;
  }

  inputFormatter = (item: OrganisationSelectorEntry): string => {
    return `${item.organisation.name} (${item.organisation.organisationRef})`;
  }

  public computeOrganisations(disabledOrgs: string[] = []): void {
    this.organisations = [];
    this._disabledOrganisations = disabledOrgs;
    let disabled = this._disabledOrganisations.indexOf(this._orgTree.organisation.organisationRef) >= 0
    this.addOrganisation(this._orgTree, 0, disabled);
  }
  
  private addOrganisation(org: OrganisationTree, index: number, disabled: boolean): void {
    if (!disabled && this._disabledOrganisations.indexOf(org.organisation.organisationRef) >= 0) {
      console.log('Disabling the organisation as it\'s found', org);
      disabled = true;
    }

    this.organisations.push({ level: index, organisation: org.organisation, disabled: disabled });
    org.children.forEach(c => this.addOrganisation(c, index+1, disabled));
  }

  onSelected(event: NgbTypeaheadSelectItemEvent<OrganisationSelectorEntry>, input: any): void {
    event.preventDefault();

    if (event.item.disabled)
      return;

    let selectedOrganisation = event.item?.organisation;
    this.organisationSelected.emit(selectedOrganisation);
    input.value = this.clearAfterSelection ? '' : selectedOrganisation?.name;
    input.blur(); // unfocus the dropdown after the selection complete
  }
}
